import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BackButton } from '../settings/settings-primitives';
import { MainContext } from '../../../context';
import { DelayedLoadIndicator } from '../../../components/delayed-load-indicator';
import { ExtensionDetailView } from '../../../components/extension/extension-detail-view';
import { UserSettingsRoutes } from '../user-settings-routes';
import { useDeleteUserExtensionVersions, useUserExtension } from './use-user-extension';
export const ExtensionSettings = props => {
    const { handleError } = useContext(MainContext);
    const navigate = useNavigate();
    const backState = (useLocation().state ?? {});
    const backTo = backState.backTo ?? UserSettingsRoutes.EXTENSIONS;
    const backLabel = backState.backLabel ?? 'Back to your extensions';
    const { data: extension, isFetching: loading, error, refetch } = useUserExtension({ namespace: props.namespace, extension: props.extension });
    const { mutateAsync: deleteVersions } = useDeleteUserExtensionVersions();
    useEffect(() => {
        if (!error) {
            return;
        }
        handleError(error);
        // The extension doesn't exist (or isn't yours) — don't stay on a dead page.
        if (error.status === 404) {
            navigate(UserSettingsRoutes.EXTENSIONS);
        }
    }, [error, navigate, handleError]);
    if (loading) {
        return _jsx(DelayedLoadIndicator, { loading: true });
    }
    if (!extension) {
        return null;
    }
    return (_jsxs(Box, { children: [_jsx(BackButton, { disableRipple: true, onClick: () => navigate(backTo), startIcon: _jsx(ArrowBackIcon, { sx: { fontSize: '0.9375rem' } }), sx: { mb: '1.375rem' }, children: backLabel }), _jsx(ExtensionDetailView, { extension: extension, onRemoveVersion: targets => deleteVersions({
                    namespace: extension.namespace,
                    extension: extension.name,
                    targetPlatformVersions: targets
                }), onVersionDeleted: refetch })] }));
};
//# sourceMappingURL=extension-settings.js.map